// @file Helper for TA-related logic, e.g. links
import environment from '@@/bits/environment'
import { captureException } from '@@/bits/error_tracker'
import { transformUrl } from '@@/bits/location'

const getPadletTaUrl = (): string => {
  return environment === 'development' ? 'https://arcade.padlet.dev' : 'https://arcade.padlet.com'
}

// Campaign -- Identifies a specific product promotion or strategic campaign.
const UTM_CAMPAIGN = 'promote_ta_on_padlet_app'

// Identifies what type of link was used, such as email or pay-per-click advertising.
// `attachment_picker_*` tag actions inside the arcade attachment picker; `arcade_attachment_*` tag
// opening an existing arcade attachment on the board. All values keep the `padlet_com_` prefix so a
// single PostHog `utm_medium` breakdown lines these up alongside the existing nav mediums.
const UTM_MEDIUM_MAPPING = {
  dashboard: 'padlet_com_app_nav_dashboard',
  home: 'padlet_com_app_nav_home',
  home_footer: 'padlet_com_app_nav_home_footer',
  attachment_picker_create_game: 'padlet_com_attachment_picker_create_game',
  attachment_picker_select_community_game: 'padlet_com_attachment_picker_select_community_game',
  attachment_picker_select_personal_game: 'padlet_com_attachment_picker_select_personal_game',
  arcade_attachment_click: 'padlet_com_arcade_attachment_click',
  arcade_attachment_expanded_view: 'padlet_com_arcade_attachment_expanded_view',
  arcade_attachment_slideshow: 'padlet_com_arcade_attachment_slideshow',
} as const

type TaUtmContext = keyof typeof UTM_MEDIUM_MAPPING

// Identifies what specifically was clicked to bring the user to the site
const UTM_CONTENT_MAPPING = {
  mobile: 'padlet_com_nav_mobile',
  desktop: 'padlet_com_nav_desktop',
  agnostic: 'padlet_com_nav',
}

// Shared builder for the four TA `utm_*` params, used both to build a fresh TA URL
// (getTaUrlWithUtmParams) and to decorate an existing arcade game URL (appendTaUtmParams).
function buildTaUtmSearchParams(
  context: TaUtmContext,
  isMobile: boolean | null, // true, false, null
  isLoggedIn: boolean,
  extraParams?: Record<string, string>,
): URLSearchParams {
  const utmMedium = UTM_MEDIUM_MAPPING[context]
  const utmContent = UTM_CONTENT_MAPPING[isMobile === null ? 'agnostic' : isMobile ? 'mobile' : 'desktop']

  // We use utm_medium for context (dashboard, home, home_footer)
  // We use utm_content for mobile or desktop or agnostic (null)
  const searchParams = new URLSearchParams({
    utm_source: `padlet_com_${environment}`, // padlet_com_development, padlet_com_staging, padlet_com_production
    utm_medium: utmMedium,
    utm_campaign: UTM_CAMPAIGN,
    utm_content: utmContent,
  })

  // `logged_in=true` allows arcade.padlet.com to get session from padlet.com and automatically log the user in
  // If the user is not logged in (e.g. on landside), it'll redirect the user to padlet.com/auth/login which is not what we want
  if (isLoggedIn) {
    searchParams.append('logged_in', 'true')
  }

  if (extraParams != null) {
    for (const [key, value] of Object.entries(extraParams)) {
      searchParams.append(key, value)
    }
  }

  return searchParams
}

function getTaUrlWithUtmParams(
  context: TaUtmContext,
  isMobile: boolean | null, // true, false, null
  isLoggedIn = false,
  options?: { path?: string; extraParams?: Record<string, string> },
): string {
  const searchParams = buildTaUtmSearchParams(context, isMobile, isLoggedIn, options?.extraParams)

  return transformUrl(getPadletTaUrl(), {
    path: options?.path,
    search: searchParams,
  })
}

// Appends the TA `utm_*` params to an existing arcade game URL, merging into (not replacing) any
// existing query params so a render-time param like `embed=1` is preserved. Used to attribute opening
// an arcade attachment (click / expanded view / slideshow) to PostHog with the right utm_medium.
// Resilient like createArcadeEmbedUrl: returns the original URL unchanged if it cannot be parsed.
function appendTaUtmParams(
  url: string,
  context: TaUtmContext,
  options?: { isMobile?: boolean | null; isLoggedIn?: boolean },
): string {
  try {
    const searchParams = buildTaUtmSearchParams(context, options?.isMobile ?? null, options?.isLoggedIn ?? false)
    // transformUrl rebuilds the URL and only keeps a hash when one is passed explicitly, so forward the
    // original fragment to preserve it (createArcadeEmbedUrl relied on this before utm params existed).
    return transformUrl(url, { searchParams, hash: new URL(url).hash })
  } catch (e) {
    // Fall back to the undecorated URL so the link still works, but report the unexpected parse
    // failure rather than swallowing it silently.
    captureException(e, { context: { url, utmContext: context } })
    return url
  }
}

// Exposes the raw utm_medium value for a context so non-URL callers (e.g. a PostHog custom event for
// picker selections, which has no page-load URL to carry the param) reuse the same mapping.
function getTaUtmMedium(context: TaUtmContext): string {
  return UTM_MEDIUM_MAPPING[context]
}

// Removes the `logged_in=true` param from an arcade URL. The param tells arcade.padlet.com to adopt
// the padlet.com session, but the external system browser opened by the native bridge does not share
// the in-app webview session, so the param would bounce the user to padlet.com/auth/login. Returns the
// URL unchanged if it cannot be parsed.
function stripLoggedInParam(url: string): string {
  try {
    const urlObj = new URL(url)
    urlObj.searchParams.delete('logged_in')
    return urlObj.toString()
  } catch {
    return url
  }
}

export { appendTaUtmParams, getPadletTaUrl, getTaUrlWithUtmParams, getTaUtmMedium, stripLoggedInParam }
export type { TaUtmContext }
